import { useState } from 'react';
import useSWR from 'swr';
import { adminApi, type AdminUser } from '../../api/admin';
import { useAuth } from '../../auth/AuthContext';

type NewUser = {
  email: string;
  name: string;
  role: 'admin' | 'user';
};

const EMPTY: NewUser = { email: '', name: '', role: 'user' };

export function UsersTab() {
  const { session } = useAuth();
  const { data: users = [], mutate } = useSWR('admin/users', () => adminApi.listUsers());
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState<NewUser>(EMPTY);
  const [err, setErr] = useState<string | null>(null);
  const [busyEmail, setBusyEmail] = useState<string | null>(null);

  async function create(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    try {
      await adminApi.createUser({
        email: form.email.trim().toLowerCase(),
        name: form.name.trim() || undefined,
        role: form.role,
        enabled: true,
      });
      setForm(EMPTY);
      setAdding(false);
      await mutate();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }

  async function toggleEnabled(u: AdminUser) {
    setErr(null);
    setBusyEmail(u.email);
    try {
      await adminApi.updateUser(u.email, { enabled: !u.enabled });
      await mutate();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyEmail(null);
    }
  }

  async function toggleRole(u: AdminUser) {
    setErr(null);
    setBusyEmail(u.email);
    try {
      await adminApi.updateUser(u.email, { role: u.role === 'admin' ? 'user' : 'admin' });
      await mutate();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyEmail(null);
    }
  }

  async function remove(u: AdminUser) {
    if (!confirm(`Remove ${u.email}? They'll be signed out and lose access immediately.`)) return;
    setErr(null);
    setBusyEmail(u.email);
    try {
      await adminApi.deleteUser(u.email);
      await mutate();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyEmail(null);
    }
  }

  function fmt(ts: string | null): string {
    if (!ts) return 'never';
    const d = new Date(ts.includes('T') ? ts : ts.replace(' ', 'T') + 'Z');
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-cockpit-dim">
          Users ({users.length})
        </h3>
        <button
          type="button"
          onClick={() => setAdding((a) => !a)}
          className="rounded border border-cockpit-border px-3 py-1 text-xs hover:bg-cockpit-muted"
        >
          {adding ? 'Cancel' : '+ Add user'}
        </button>
      </div>

      {err && (
        <div className="rounded border border-cockpit-crit bg-cockpit-crit/10 p-2 text-xs text-cockpit-crit">
          {err}
        </div>
      )}

      {adding && (
        <form
          onSubmit={create}
          className="grid grid-cols-1 gap-3 rounded border border-cockpit-border bg-cockpit-panel p-4 text-xs lg:grid-cols-4"
        >
          <label className="flex flex-col gap-1 lg:col-span-2">
            <span className="text-cockpit-dim">Email</span>
            <input
              type="email"
              required
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="rounded border border-cockpit-border bg-cockpit-bg px-2 py-1 text-cockpit-text"
              placeholder="someone@insightdelivered.com"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-cockpit-dim">Name (optional)</span>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="rounded border border-cockpit-border bg-cockpit-bg px-2 py-1 text-cockpit-text"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-cockpit-dim">Role</span>
            <select
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value as 'admin' | 'user' })}
              className="rounded border border-cockpit-border bg-cockpit-bg px-2 py-1 text-cockpit-text"
            >
              <option value="user">user</option>
              <option value="admin">admin</option>
            </select>
          </label>
          <div className="lg:col-span-4">
            <button
              type="submit"
              className="rounded bg-cockpit-text px-3 py-1 text-xs text-cockpit-bg"
            >
              Add
            </button>
          </div>
        </form>
      )}

      <div className="overflow-hidden rounded border border-cockpit-border">
        <table className="w-full text-sm">
          <thead className="bg-cockpit-panel text-xs uppercase tracking-wider text-cockpit-dim">
            <tr>
              <th className="px-3 py-2 text-left">Email</th>
              <th className="px-3 py-2 text-left">Name</th>
              <th className="px-3 py-2 text-left">Role</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2 text-left">Last seen</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => {
              const isMe = session.user?.email === u.email;
              const busy = busyEmail === u.email;
              return (
                <tr key={u.email} className="border-t border-cockpit-border">
                  <td className="px-3 py-2 font-mono text-xs">
                    {u.email}
                    {isMe && <span className="ml-2 text-[10px] text-cockpit-dim">(you)</span>}
                  </td>
                  <td className="px-3 py-2 text-xs">{u.name ?? '—'}</td>
                  <td
                    className={`px-3 py-2 text-xs uppercase ${u.role === 'admin' ? 'text-cockpit-warn' : 'text-cockpit-dim'}`}
                  >
                    {u.role}
                  </td>
                  <td
                    className={`px-3 py-2 text-xs ${u.enabled ? 'text-cockpit-ok' : 'text-cockpit-crit'}`}
                  >
                    {u.enabled ? 'enabled' : 'disabled'}
                  </td>
                  <td className="px-3 py-2 text-xs text-cockpit-dim">{fmt(u.last_seen_at)}</td>
                  <td className="px-3 py-2 text-right">
                    <button
                      type="button"
                      disabled={busy || isMe}
                      onClick={() => void toggleRole(u)}
                      className="mr-2 text-xs text-cockpit-dim hover:text-cockpit-text disabled:opacity-30"
                    >
                      {u.role === 'admin' ? 'Demote' : 'Promote'}
                    </button>
                    <button
                      type="button"
                      disabled={busy || isMe}
                      onClick={() => void toggleEnabled(u)}
                      className="mr-2 text-xs text-cockpit-dim hover:text-cockpit-text disabled:opacity-30"
                    >
                      {u.enabled ? 'Disable' : 'Enable'}
                    </button>
                    <button
                      type="button"
                      disabled={busy || isMe}
                      onClick={() => void remove(u)}
                      className="text-xs text-cockpit-crit hover:underline disabled:opacity-30"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              );
            })}
            {users.length === 0 && (
              <tr>
                <td colSpan={6} className="px-3 py-6 text-center text-sm text-cockpit-dim">
                  no users yet
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
